import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, DialogComponent } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';

import { Store } from '@ngrx/store';
import { Subject, switchMap, take, takeUntil } from 'rxjs';
import {
  CropAid,
  OrderLine,
  OrderLineStatus,
  Package,
  SalesOrderPeriod,
  SiteState,
} from 'src/app/shared/models';

import { OrderplanningService } from 'src/app/shared/services/orderplanning.service';

import { createOrder, loadSalesOrderPeriods, saveOrderLine } from '../../store/actions';

import { selectOrderLines, selectSalesOrderPeriodsFromStore } from '../../store/selectors';
import { selectCropYear } from '../../../store/selectors';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatListItemIcon, MatListItemTitle, MatListItemLine } from '@angular/material/list';

export interface ConfirmOrderDialogDataOverview extends AbstractDialogData {
  cropAids: CropAid[];
  packages: Package[];
  cartLineCount: number;
}

@Component({
    selector: 'app-confirm-order-dialog',
    templateUrl: './confirm-order-dialog.component.html',
    styleUrls: ['./confirm-order-dialog.component.scss'],
    imports: [
        DialogComponent,
        MatIcon,
        MatList,
        MatListItem,
        MatListItemIcon,
        MatListItemTitle,
        MatListItemLine,
        TranslatePipe,
        AgrNumberPipe,
    ],
})
export class ConfirmOrderDialogOverviewComponent extends AbstractDialog implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly orderplanningService = inject(OrderplanningService);
  data = inject<ConfirmOrderDialogDataOverview>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef = inject<MatDialogRef<ConfirmOrderDialogOverviewComponent, AbstractDialogResultData<never>>>(MatDialogRef);

  destroy$ = new Subject<boolean>();
  orderLines: OrderLine[] | undefined;
  salesOrderPeriods: SalesOrderPeriod[] = [];
  preferredDeliveryPeriods: number[] = [];

  ngOnInit(): void {
    this.selectOrderLines();
    this.selectSalesOrderPeriods();
    this.loadCustomerPreferences();
  }

  loadCustomerPreferences(): void {
    this.store.select(selectCropYear).pipe(
      take(1),
      switchMap((cropYear) => this.orderplanningService.getCustomerPreferences(cropYear)),
      takeUntil(this.destroy$),
    ).subscribe((preferences) => {
      this.preferredDeliveryPeriods = preferences.preferedDeliveryPeriods || [];
    });
  }

  selectOrderLines(): void {
    this.store.select(selectOrderLines).pipe(takeUntil(this.destroy$)).subscribe((ol) => {
      this.orderLines = ol.filter((o) => o.status === OrderLineStatus.open);
    });
  }

  getCropAid(id: number | undefined) {
    return this.findCropAid(id, this.data.cropAids);
  }

  private findCropAid(id: number | undefined, cropAids: CropAid[]): CropAid | undefined {
    return cropAids.find((cropAid) => cropAid.id === id);
  }

  getCropAidCodeToDisplay(id: number | undefined) {
    return this.getCropAidCodeToDisplayPure(id, this.data.cropAids);
  }

  private getCropAidCodeToDisplayPure(id: number | undefined, cropAids: CropAid[]): string | undefined {
    if (!id) {
      return;
    }
    const cropAidCode = this.findCropAid(id, cropAids)?.codes[0];
    if (!cropAidCode) {
      return;
    }
    return `(${cropAidCode})`;
  }

  getPackageAmount(packageId: number): number {
    return this.findPackageAmount(packageId, this.data.packages);
  }

  private findPackageAmount(packageId: number, packages: Package[]): number {
    const pkg = packages.find((p) => p.id === packageId);
    return pkg ? pkg.amount : 0;
  }

  getPackageUnitOfMeasurement(packageId: number): string {
    return this.findPackageUnitOfMeasurement(packageId, this.data.packages);
  }

  private findPackageUnitOfMeasurement(packageId: number, packages: Package[]): string {
    const pkg = packages.find((p) => p.id === packageId);
    return pkg ? pkg.unitOfMeasurement : '';
  }

  showAmountWarning(): boolean {
    return !this.orderLines?.some((ol) => ol.status === 'Open' && ol.count > 0);
  }

  selectSalesOrderPeriods(): void {
    this.store.select(selectCropYear).pipe(take(1)).subscribe((cropYear) => {
      this.store.dispatch(loadSalesOrderPeriods({ year: cropYear }));
    });
    this.store.select(selectSalesOrderPeriodsFromStore).pipe(takeUntil(this.destroy$)).subscribe((periods) => {
      this.salesOrderPeriods = periods;
    });
  }

  getSalesOrderPeriod(periodId: number): SalesOrderPeriod | undefined {
    return this.salesOrderPeriods.find((p) => p.id === periodId);
  }

  getOrderLinesByPeriod(periodId: number): OrderLine[] {
    return this.filterOrderLinesByPeriod(periodId, this.orderLines || [], this.preferredDeliveryPeriods);
  }

  private filterOrderLinesByPeriod(
    periodId: number,
    orderLines: OrderLine[],
    preferredDeliveryPeriods: number[],
  ): OrderLine[] {
    return orderLines.filter((ol) => {
      const orderLinePeriodId = ol.periodId;

      // If orderLine's period is in preferred periods, use it directly
      if (preferredDeliveryPeriods.includes(orderLinePeriodId)) {
        return orderLinePeriodId === periodId;
      }

      // If orderLine's period is NOT in preferred periods, find next available preferred period
      const adjustedPeriodId = this.getNextPreferredPeriodPure(orderLinePeriodId, preferredDeliveryPeriods);
      return adjustedPeriodId === periodId;
    });
  }

  getNextPreferredPeriod(originalPeriodId: number): number {
    return this.getNextPreferredPeriodPure(originalPeriodId, this.preferredDeliveryPeriods);
  }

  private getNextPreferredPeriodPure(originalPeriodId: number, preferredDeliveryPeriods: number[]): number {
    if (!preferredDeliveryPeriods.length) {
      return originalPeriodId;
    }

    const sortedPeriods = [...preferredDeliveryPeriods].sort((a: number, b: number) => a - b);
    const nextPeriod = sortedPeriods.find((p: number) => p >= originalPeriodId);

    if (nextPeriod) {
      return nextPeriod;
    }

    return preferredDeliveryPeriods[preferredDeliveryPeriods.length - 1];
  }

  getUniquePeriodIds(): number[] {
    return this.getUniquePeriodIdsPure(this.orderLines || [], this.preferredDeliveryPeriods);
  }

  private getUniquePeriodIdsPure(orderLines: OrderLine[], preferredDeliveryPeriods: number[]): number[] {
    const adjustedPeriodIds = new Set<number>();

    orderLines.forEach((ol) => {
      const orderLinePeriodId = ol.periodId;

      if (preferredDeliveryPeriods.includes(orderLinePeriodId)) {
        adjustedPeriodIds.add(orderLinePeriodId);
      } else {
        const adjustedPeriodId = this.getNextPreferredPeriodPure(orderLinePeriodId, preferredDeliveryPeriods);
        adjustedPeriodIds.add(adjustedPeriodId);
      }
    });

    return Array.from(adjustedPeriodIds).sort((a, b) => a - b);
  }

  formatPeriodHeader(periodId: number): string {
    const period = this.getSalesOrderPeriod(periodId);
    if (!period) {
      return '';
    }
    const fromDate = new Date(period.startDispatchFrom).toLocaleDateString();
    const untilDate = new Date(period.deliverBefore).toLocaleDateString();
    const fromTranslation = this.locale.translate('from');
    const toTranslation = this.locale.translate('to');
    return `${period.name} ${fromTranslation} ${fromDate} ${toTranslation} ${untilDate}`;
  }

  /*
    A mat-dialog adds a div with class 'cdk-global-overlay-wrapper' to a
    div with class 'cdk-overlay-container' at the bottom of the .html,
    so the dialogs get nested behind one another.
    The ConfirmOrder dialog gets automatically dismissed and makes
    SelectPeriodsToOrder dialog to be dismissed with it.

    This function overrides the code from the effects -> confirmOrder.

    This is a fix for the nested dialog that closes immediately after it's opened
  */
  confirm(): void {
    // First, adjust orderLines to preferred periods if needed
    this.adjustOrderLinesToPreferredPeriods();

    // Then create the order
    this.store.dispatch(createOrder());
  }

  adjustOrderLinesToPreferredPeriods(): void {
    if (!this.preferredDeliveryPeriods.length || !this.orderLines) {
      return;
    }

    const orderLinesToUpdate = this.calculateOrderLinesToUpdate(
      this.orderLines,
      this.preferredDeliveryPeriods,
    );

    // Save all updated orderLines
    orderLinesToUpdate.forEach((orderLine) => {
      this.store.dispatch(saveOrderLine({ orderLine }));
    });
  }

  private calculateOrderLinesToUpdate(
    orderLines: OrderLine[],
    preferredDeliveryPeriods: number[],
  ): OrderLine[] {
    const orderLinesToUpdate: OrderLine[] = [];

    orderLines.forEach((orderLine) => {
      const originalPeriodId = orderLine.periodId;

      // If orderLine's period is not in preferred periods, adjust it
      if (!preferredDeliveryPeriods.includes(originalPeriodId)) {
        const adjustedPeriodId = this.getNextPreferredPeriodPure(originalPeriodId, preferredDeliveryPeriods);

        if (adjustedPeriodId !== originalPeriodId) {
          // Create updated orderLine with new periodId
          const updatedOrderLine = {
            ...orderLine,
            periodId: adjustedPeriodId,
          };
          orderLinesToUpdate.push(updatedOrderLine);
        }
      }
    });

    return orderLinesToUpdate;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
